import { GlobalHeader } from "@cmsgov/ds-medicare-gov";
import React, { useContext } from "react";
import { AppStore } from "../../app/store";
import { RouteComponentProps, withRouter } from "react-router";
import { UserLanguage, ActionType, MctToastType } from "../../@types";
import {
  sendTealiumEvent,
  useTranslate,
  logError,
  getBeneFirstNameAndLastInitial,
} from "../../helpers";

import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  hasLoginSession,
  initiateLogin,
  getSlsxSignoutUrl,
} from "../../helpers/loginHelpers";
import { sendLogoutRequest } from "../../api";
import { BeneficiaryInfo } from "../";
import { launchChat } from "../../helpers/chatHelpers";

interface HeaderProps {
  productName?: string;
  productLink?: string;
  noBorder?: boolean;
  variation?: string;
  excludeUserInfo?: boolean;
  hideLogin?: boolean;
}

const Header: React.FunctionComponent<HeaderProps & RouteComponentProps> = ({
  productName,
  productLink,
  noBorder,
  variation,
  history,
  location,
  excludeUserInfo,
  hideLogin,
}) => {
  const {
    state: { csr, beneficiary, language },
    dispatch,
  } = useContext(AppStore.AppContext);

  const t = useTranslate();
  const { feEnableAuthentication, feEnableChat } = useFlags();

  const isEnglish = language === UserLanguage.ENGLISH;

  const switchLanguage = (): void => {
    sendTealiumEvent("Header", isEnglish ? "Espanol" : "English");

    dispatch({
      type: ActionType.UPDATE_LANGUAGE,
      payload: isEnglish ? UserLanguage.SPANISH : UserLanguage.ENGLISH,
    });

    const uri = new URI(location.search);
    uri.setQuery(
      "lang",
      isEnglish ? UserLanguage.SPANISH : UserLanguage.ENGLISH
    );
    location.search = uri.search();
    history.replace(location);
  };

  const loggedIn = hasLoginSession() && beneficiary;

  const logout = async (): Promise<void> => {
    try {
      await sendLogoutRequest();
    } catch (e) {
      logError("Could not log out user (LoginLogoutButton)", e);
    }

    history.push(`/?mctt=${MctToastType.LOGGED_OUT}`);

    dispatch({ type: ActionType.RESET_STATE });

    if (!csr) {
      const signoutUrl = getSlsxSignoutUrl();
      window.location.href = signoutUrl;
    }
  };

  const login = (): void => {
    initiateLogin(language === UserLanguage.SPANISH);
  };

  let loginButtonText = "";
  let loginFunction = (): void => {};

  // It's important to check if this variable is false because it can be undefined initially
  if (feEnableAuthentication === false && !loggedIn) {
    loginButtonText = t("app.login_unavailable");
  } else if (loggedIn) {
    loginButtonText = t("app.menu.logout");
    loginFunction = logout;
  } else {
    loginButtonText = t("app.menu.login");
    loginFunction = login;
  }

  const actions = [];

  if (!excludeUserInfo && beneficiary) {
    actions.push({
      text: getBeneFirstNameAndLastInitial(beneficiary, t),
      menuPanel: <BeneficiaryInfo bene={beneficiary} />,
    });
  }

  if (loggedIn && feEnableChat) {
    actions.push({
      text: (
        <>
          <i className="fas fa-comments ds-u-margin-right--1" />
          {t("app.menu.live_chat")}
        </>
      ),
      callback: (): Promise<boolean> => launchChat(language, location.pathname),
    });
  }

  if (!hideLogin) {
    actions.push({
      text: loginButtonText,
      callback: loginFunction,
      props: { id: "mct-login-button" },
    });
  }

  actions.push({
    text: isEnglish ? "Español" : "English",
    callback: switchLanguage,
  });

  return (
    <GlobalHeader
      productName={productName}
      productLink={productLink}
      variation={variation}
      noBorder={noBorder}
      actions={actions}
      menuButtonAriaLabel={t("navigation_menu")}
    />
  );
};

export default withRouter(Header);
